import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import DecidedBy from "../../components/common/DecidedBy";
import TestDataBadge from "../../components/common/TestDataBadge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";
import { Skeleton } from "../../components/ui/skeleton";
import { Sparkles, Send, Loader2, ChevronLeft, ChevronRight, MapPin, Plus, X } from "lucide-react";
import { toast } from "sonner";
import {
  currentMonthIST, monthDaysIST, monthLabelIST, todayIST, formatISTDayLabel,
} from "../../lib/datetime";
import { plural } from "../../lib/vocabulary";
import StickyHeading from "../../components/common/StickyHeading";
import { joinParts } from "../../lib/vocabulary";
import { entryDoctorsLabel, plannedDoctorsLabel } from "./plannedDoctors";
import PlannedDayCard from "../../components/tour-plans/PlannedDayCard";

const STATUS_LABEL = { Draft: "Draft", Submitted: "Submitted", Approved: "Approved" };
const STATUS_CLS = {
  Draft: "bg-slate-100 text-slate-700",
  Submitted: "bg-amber-50 text-amber-700",
  Approved: "bg-emerald-50 text-emerald-700",
};

export default function MyTourPlanPage() {
  const { user } = useAuth();
  // The month this page opens on is the COMPANY's month. `getMonth()` reads the
  // browser's zone, so on the 1st before 05:30 IST — and on the last day after
  // 18:30 IST for a viewer west of UTC — the planner opened on the wrong month
  // and the officer planned into a grid the server was not looking at.
  const openOn = currentMonthIST();
  const [year, setYear] = useState(openOn.year);
  const [month, setMonth] = useState(openOn.month);
  const [plan, setPlan] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmThin, setConfirmThin] = useState(false);
  const [editDay, setEditDay] = useState(null); // iso

  async function load() {
    setLoading(true);
    setError("");
    try {
      const [{ data: p }, { data: docs }] = await Promise.all([
        api.get("/tour-plans/my", { params: { year, month } }),
        // Only doctors this person owns — for a Sales Manager an unscoped
        // /doctors also returns their team's, which the backend rejects on save.
        api.get("/doctors", { params: { assigned_to: user.id } }),
      ]);
      setPlan(p);
      setDoctors(docs);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  const { days } = useMemo(() => monthDaysIST(year, month), [year, month]);
  const byDate = useMemo(() => {
    const map = {};
    for (const e of plan?.daily_plan || []) map[e.date] = e;
    return map;
  }, [plan]);

  // The doctor count comes from the SERVER, which is the only side that can
  // tell "named nobody" from "never named anyone" — see ./plannedDoctors.
  const totals = useMemo(() => ({ entries: (plan?.daily_plan || []).length }), [plan]);

  const locked = plan?.status === "Submitted" || plan?.status === "Approved";

  // TODAY, WALKED. The card renders only when the grid on screen is the
  // current month and the plan exists on the server — a plan with no id has
  // no day to ask progress for, and a past or future month has no "today".
  const today = todayIST();
  const showToday = !!plan?.id && days.some((d) => d.iso === today);

  // WHY THE GRID IS DEAD, said on the page. A locked plan disabled every day
  // and hid Submit, and nothing explained it — officers reported it as "the
  // page is not working". One sentence, rendered above the grid AND used by
  // the grid's tap handler, so the notice and the toast cannot drift.
  //
  // The approver's name is optional: a plan can be Submitted with nobody
  // named yet. "your manager" stands in — never the string "undefined", which
  // is what any bare interpolation of a missing name renders.
  const lockedMessage = useMemo(() => {
    if (!locked) return null;
    const approver = (plan?.approver_name || "").trim() || "your manager";
    return plan?.status === "Approved"
      ? `This plan is approved and locked. To add more days this month, ask ${approver} to reopen it.`
      : `This plan is with ${approver} for approval. You cannot add or change days until it is approved or sent back to you.`;
  }, [locked, plan?.status, plan?.approver_name]);

  function onGridClick() {
    if (lockedMessage) toast(lockedMessage);
  }

  // HOW MUCH OF THE MONTH IS STILL AHEAD. Counted from the grid's own days, so
  // a future month is entirely ahead and a finished one entirely behind — the
  // nudge below must not fire on a month there is no longer time to plan.
  const daysLeftInMonth = useMemo(() => {
    if (!days.length) return 0;
    const today = todayIST();
    if (today > days[days.length - 1].iso) return 0;
    if (today < days[0].iso) return days.length;
    return days.filter((d) => d.iso > today).length;
  }, [days]);

  // THE NUDGE, NOT A BLOCK. All three officers submitted one-day plans on
  // go-live day, and a one-day plan is what made "the plan is locked" a
  // problem worth a whole feature. So a thin plan asks once and then submits
  // whatever the officer decides — a refusal here would invent a rule nobody
  // agreed to, which is the failure this module already has a history of.
  const thinPlan = totals.entries < 5 && daysLeftInMonth > 7;
  const thinPlanMessage =
    `You have planned only ${totals.entries} day(s) of ${monthLabelIST(year, month)}. ` +
    "A tour plan is meant to cover the month. Submit anyway?";

  async function saveDailyPlan(newDaily) {
    try {
      const { data } = await api.post("/tour-plans", { year, month, daily_plan: newDaily });
      setPlan(data);
      return data;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      throw e;
    }
  }

  function onSubmit() {
    if (thinPlan) {
      setConfirmThin(true);
      return;
    }
    return doSubmit();
  }

  async function doSubmit() {
    setConfirmThin(false);
    if (!plan?.id) {
      // Need to save draft first (POST creates if absent).
      await saveDailyPlan(plan?.daily_plan || []);
    }
    setSubmitting(true);
    try {
      const id = plan?.id || (await api.get("/tour-plans/my", { params: { year, month } })).data?.id;
      const { data } = await api.put(`/tour-plans/${id}/submit`);
      setPlan(data);
      toast.success("Submitted for approval");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  }

  function shift(delta) {
    let nm = month + delta;
    let ny = year;
    if (nm < 1) {
      nm = 12;
      ny -= 1;
    } else if (nm > 12) {
      nm = 1;
      ny += 1;
    }
    setYear(ny);
    setMonth(nm);
  }

  return (
    <div className="space-y-6" data-testid="my-tour-plan-page">
      <StickyHeading
        icon={Sparkles}
        label="My Tour Plan"
        title={monthLabelIST(year, month)}
        subtitle={
          <>
            {`${plural(totals.entries, "day")} planned`} · {plannedDoctorsLabel(plan)} ·{" "}
            <span className={`px-1.5 py-0.5 rounded-full text-[11px] font-bold ${STATUS_CLS[plan?.status || "Draft"]}`}>
              {STATUS_LABEL[plan?.status || "Draft"]}
            </span>
          </>
        }
        testId="my-tour-plan-heading"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => shift(-1)} className="h-10 w-10 p-0 border-callisto" data-testid="tour-plan-prev">
              <ChevronLeft size={16} />
            </Button>
            <Button variant="outline" onClick={() => shift(1)} className="h-10 w-10 p-0 border-callisto" data-testid="tour-plan-next">
              <ChevronRight size={16} />
            </Button>
            {!locked && (
              <Button
                onClick={onSubmit}
                disabled={submitting || totals.entries === 0}
                className="h-10 bg-callisto-navy hover:bg-callisto-navy/90 text-white"
                data-testid="tour-plan-submit"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Send size={14} className="mr-1.5" /> Submit plan</>)}
              </Button>
            )}
          </div>
        }
      />

      {plan?.id && (
        // The officer's own plan-vs-actual. It lived only on the detail page,
        // which nothing on this page linked to — so the one person the
        // comparison is about was the one person who could not open it.
        <Link
          to={`/route-planner/${plan.id}`}
          className="inline-block text-xs font-semibold text-callisto-gold hover:underline"
          data-testid="my-tour-plan-vs-actual-link"
        >
          Plan vs actual →
        </Link>
      )}

      <TestDataBadge record={plan} className="mb-1" />
      <DecidedBy record={plan} className="text-[11px]" />
      {plan?.approver_remark && (
        <div className="rounded-lg border border-orange-200 bg-orange-50 text-orange-800 px-3 py-2 text-sm">
          {plan.approver_remark}
        </div>
      )}
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>
      )}
      {lockedMessage && (
        <div
          className="rounded-lg border border-orange-200 bg-orange-50 text-orange-800 px-3 py-2 text-sm"
          data-testid="tour-plan-locked-notice"
        >
          {lockedMessage}
        </div>
      )}

      {!loading && showToday && <PlannedDayCard planId={plan.id} date={today} />}

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {Array.from({ length: 12 }, (_, i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
      ) : (
        <div
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-2"
          data-testid="tour-plan-grid"
          onClick={onGridClick}
        >
          {days.map((d) => {
            const entry = byDate[d.iso];
            const isPast = d.iso < todayIST();
            const isToday = d.iso === todayIST();
            return (
              <button
                key={d.iso}
                type="button"
                disabled={locked && !entry}
                onClick={() => !locked && setEditDay(d.iso)}
                data-testid={`tour-plan-day-${d.iso}`}
                className={`text-left rounded-lg border p-2.5 transition-all min-h-[88px] ${
                  entry
                    ? "border-callisto-gold/40 bg-callisto-gold/5 hover:-translate-y-0.5 hover:shadow-md"
                    : isPast
                    ? "border-callisto bg-slate-50/50 text-callisto-muted"
                    : "border-callisto bg-white hover:border-callisto-navy/40"
                } ${isToday ? "ring-1 ring-callisto-navy" : ""} ${locked && !entry ? "cursor-default" : "cursor-pointer"}`}
              >
                <div className="flex items-baseline justify-between">
                  <span className="text-base font-bold text-callisto-navy">{d.day}</span>
                  {d.weekday === 0 && <span className="text-[9px] uppercase text-callisto-muted">Sun</span>}
                </div>
                {entry ? (
                  <>
                    {/* `area` is a free-text locality the officer types, not a
                        territory unit, so it is NOT rendered through
                        <TerritoryLabel/> — that component's absent case reads
                        "No territory unit", which would be a claim about a
                        master this field does not belong to. A row saved
                        without one still must not render a bare pin. */}
                    <p className="mt-1 text-[11px] font-semibold text-callisto-navy line-clamp-1 flex items-center gap-0.5">
                      <MapPin size={10} />
                      {entry.area || <span className="text-callisto-muted italic">No area recorded</span>}
                    </p>
                    {/* `entry.planned_doctors.length` — no `?.` — threw on
                        every entry written before the field existed, so a
                        legacy month rendered a blank grid. The gap is now
                        named instead. */}
                    <p className="text-[10px] text-callisto-muted" data-testid={`tour-plan-day-doctors-${d.iso}`}>
                      {entryDoctorsLabel(entry)}
                    </p>
                  </>
                ) : !locked && !isPast ? (
                  <p className="mt-1 text-[10px] text-callisto-muted flex items-center gap-0.5">
                    <Plus size={10} /> Plan
                  </p>
                ) : null}
              </button>
            );
          })}
        </div>
      )}

      <Dialog open={confirmThin} onOpenChange={(o) => !o && setConfirmThin(false)}>
        <DialogContent data-testid="tour-plan-thin-dialog">
          <DialogHeader>
            <DialogTitle>Submit a partial month?</DialogTitle>
            <DialogDescription>{thinPlanMessage}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmThin(false)} data-testid="tour-plan-thin-cancel">
              Cancel
            </Button>
            <Button
              onClick={doSubmit}
              className="bg-callisto-navy hover:bg-callisto-navy/90 text-white"
              data-testid="tour-plan-thin-confirm"
            >
              Submit anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DayEditor
        open={!!editDay}
        date={editDay}
        plan={plan}
        doctors={doctors}
        onClose={() => setEditDay(null)}
        onSave={async (entry) => {
          const others = (plan?.daily_plan || []).filter((e) => e.date !== editDay);
          const next = entry ? [...others, entry] : others;
          await saveDailyPlan(next);
          setEditDay(null);
          toast.success(entry ? "Day saved" : "Day cleared");
        }}
      />
    </div>
  );
}

function DayEditor({ open, date, plan, doctors, onClose, onSave }) {
  const existing = useMemo(
    () => (plan?.daily_plan || []).find((e) => e.date === date),
    [plan, date]
  );
  const [area, setArea] = useState("");
  const [picked, setPicked] = useState([]);
  const [remarks, setRemarks] = useState("");

  useEffect(() => {
    if (open) {
      setArea(existing?.area || "");
      setPicked(existing?.planned_doctors || []);
      setRemarks(existing?.remarks || "");
    }
  }, [open, existing]);

  function toggle(id) {
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{formatISTDayLabel(date)}</DialogTitle>
          <DialogDescription>Set the area and pick the doctors you plan to visit.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-callisto-navy">Area / locality</Label>
            <Input
              value={area}
              onChange={(e) => setArea(e.target.value)}
              placeholder="e.g., Andheri West"
              className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
              data-testid="tour-plan-day-area"
            />
          </div>
          <div>
            <Label className="text-callisto-navy">Doctors</Label>
            <div className="mt-1.5 max-h-60 overflow-y-auto rounded-md border border-callisto divide-y divide-callisto">
              {doctors.length === 0 ? (
                <p className="p-3 text-sm text-callisto-muted">No doctors assigned to you.</p>
              ) : (
                doctors.map((d) => (
                  <label
                    key={d.id}
                    className="flex items-center gap-2.5 p-2.5 text-sm cursor-pointer hover:bg-callisto-app"
                  >
                    <Checkbox
                      checked={picked.includes(d.id)}
                      onCheckedChange={() => toggle(d.id)}
                      data-testid={`tour-plan-doctor-${d.id}`}
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-callisto-navy truncate">{d.name}</p>
                      <p className="text-[11px] text-callisto-muted truncate">
                        {joinParts([d.specialization, d.area])}
                      </p>
                    </div>
                  </label>
                ))
              )}
            </div>
            <p className="mt-1 text-[11px] text-callisto-muted">{picked.length} selected</p>
          </div>
          <div>
            <Label className="text-callisto-navy">Remarks (optional)</Label>
            <Textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={2}
              className="mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
              data-testid="tour-plan-day-remarks"
            />
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          {existing && (
            <Button
              variant="outline"
              className="h-10 border-red-200 text-red-700 hover:bg-red-50"
              onClick={() => onSave(null)}
              data-testid="tour-plan-day-clear"
            >
              <X size={14} className="mr-1" /> Clear day
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => {
              if (!area.trim()) return toast.error("Add the area name");
              onSave({ date, area: area.trim(), planned_doctors: picked, remarks: remarks.trim() || null });
            }}
            className="bg-callisto-navy hover:bg-callisto-navy/90"
            data-testid="tour-plan-day-save"
          >
            Save day
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
